import React, { memo } from 'react';
import { FocusInput } from 'common/FocusInput';
import { Control, Controller, FieldPath } from 'react-hook-form';
import { Tooltip, ElementSide } from '@tcc/fds';
import { currencyFormatter, getMessage } from 'util/locale';
import { convertPlantingPlanParams } from 'util/parse';
import { FormData, FieldData } from '../../types';

interface SeedCostInputCellProps {
  control: Control<FormData> | Control<Partial<FieldData>>;
  name: FieldPath<FormData> | Partial<keyof FieldData>;
  currency: string;
  bagSize: number;
  rowIndex: number;
  label?: string | undefined;
  children?: React.ReactNode;
  disabled?: boolean;
  showTooltip?: boolean;
}

export const SeedCostInputCell = memo(function Cell({
  control,
  name,
  currency,
  bagSize,
  rowIndex,
  label,
  disabled,
  showTooltip,
  ...rest
}: SeedCostInputCellProps) {
  return (
    <Controller
      control={control as Control<FormData>}
      name={name as FieldPath<FormData>}
      render={({ field, fieldState: { error } }) => {
        const { display, displayValue } = convertPlantingPlanParams({
          validationResult: undefined,
          value: field.value,
          currency
        });

        const input = (
          <FocusInput
            error={error?.message}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            placeholder={
              getMessage(
                'bulk-fvss-review-scripts-seed-cost-per-bag-placeholder',
                { cost: currencyFormatter(0, currency), numSeeds: bagSize / 1000 }
              )
            }
            testId={`seed-cost-input-${rowIndex}`}
            value={display}
            displayValue={displayValue}
            showReset={false}
            className="script-generation-row-input"
            label={label}
            disabled={disabled}
            {...rest}
          />
        );

        if (showTooltip) {
          return (
            <Tooltip
              testId={`seed-cost-locked-tooltip-${rowIndex}`}
              placement={ElementSide.Bottom}
              trigger={<span>{input}</span>}
            >
              {getMessage('bulk-fvss-script-generation-locked-input-tooltip')}
            </Tooltip>
          );
        }

        return input;
      }}
    />
  );
});
